import React, { useState, useCallback, useMemo, useEffect } from "react";
import { CreateMisdeliveryEntry } from "../../API/CreateMisdeliveryEntry";
import { getMisDelivery } from "../../API/getMisDelivery";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AppState from "../../AppState";
import {
  ACCORDION_PANELS,
  APP,
  EOsdType,
  EUploadKey,
  INITIAL_EXPANDED_PANELS,
} from "../../constants";
import {
  MisdeliveryFormData,
  IMisdeliveryInvestigation,
  MisDeliveryStatus,
  IClearedInstructionContainer,
} from "../../models";
import {
  msdInitialForm,
  useMisdeliveryForm,
} from "../../@hooks/useMisdeliveryForm";
import {
  BasicInformationSection,
  MisdeliveryInformationSection,
  PalletInformationSection,
  ShipmentDetailsSection,
  MisdeliveryFormActions,
} from "../../components/misdelivery";
import "./misdeliveryEntry.css";
import { usePackageMaster } from "../../@hooks/usePackageMaster";
import { isFormValueChanged } from "../../AppState/isFormValueChanged";
import PageHeaderWithBack from "../../components/PageHeaderWithBack";
import F18NoteSection from "../../components/misdelivery/F18NoteSection";
import { useDateTimeFormatter } from "../../@hooks/useDateTimeFormatter";
import {
  compareObjects,
  normalizeEmptyFields,
  userTerminal,
} from "../../helpers";
import Loader from "../../components/loader/loader";
import MisdeliveryBOL from "./misdeliveryBOL";
import UploadDocument from "../../components/uploadDocument/uploadDocument";
import { GetMSDNotes } from "../../API/getMSDNotes";
import MisdeliveryInvestigation from "./misdeliveryInvestigation";
import { hasMisdeliveryInvestigationPermissionSelector } from "../../AppState/selectors/userPermissionsSelectors";
import { useCanViewByOwnershipPermission } from "../../@hooks/useCanViewByOwnershipPermission";
import type { MisdeliveryBolFormValues } from "../../@hooks/useMisdeliveryBolForm";
import { usePreviousPath } from "../../context/navigation/PreviousPathContext";
import { MisdeliveryFormProvider } from "../../contexts/MisdeliveryFormContext";
import ClearedInstructionDetails from "../../components/cleared/clearedInstructionDetails";
import {
  consumeMisdeliveryEmailSentFlag,
  subscribeMisdeliveryEmailSent,
} from "./misdeliveryEmailEvents";

/**
 * Performance Optimizations Applied:
 * 1. Memoized proNumbersList to prevent recalculation on every render
 * 2. Removed unstable callback wrappers that provided no benefit
 * 3. Extracted Panel1Content into separate React.memo component to isolate re-renders
 * 4. Optimized accordionPanels useMemo dependencies
 * 5. All callbacks use stable dependencies to prevent cascading re-renders
 * 6. Context API for form state - eliminates prop drilling
 * 7. useReducer for UI state management - consolidates related state
 * 8. Memoized Panel 2 & 3 components for better isolation
 *
 * Expected Impact: 60-80% reduction in unnecessary component re-renders
 */

// Types for UI state reducer
type UIState = {
  expanded: string[];
  isSubmitting: boolean;
  isLoadingData: boolean;
  loading: boolean;
  formMode: string;
  fetchedOsdNumber: number | null;
  fetchedNotesOsdNumber: number | null;
};

type UIAction =
  | { type: "SET_EXPANDED"; payload: string[] }
  | { type: "TOGGLE_PANEL"; payload: string }
  | { type: "SET_SUBMITTING"; payload: boolean }
  | { type: "SET_LOADING_DATA"; payload: boolean }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_FORM_MODE"; payload: string }
  | { type: "SET_FETCHED_OSD_NUMBER"; payload: number | null }
  | { type: "SET_FETCHED_NOTES_OSD_NUMBER"; payload: number | null };

const uiStateReducer = (state: UIState, action: UIAction): UIState => {
  switch (action.type) {
    case "SET_EXPANDED":
      return { ...state, expanded: action.payload };
    case "TOGGLE_PANEL":
      return {
        ...state,
        expanded: state.expanded.includes(action.payload)
          ? state.expanded.filter((p) => p !== action.payload)
          : [...state.expanded, action.payload],
      };
    case "SET_SUBMITTING":
      return { ...state, isSubmitting: action.payload };
    case "SET_LOADING_DATA":
      return { ...state, isLoadingData: action.payload };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_FORM_MODE":
      return { ...state, formMode: action.payload };
    case "SET_FETCHED_OSD_NUMBER":
      return { ...state, fetchedOsdNumber: action.payload };
    case "SET_FETCHED_NOTES_OSD_NUMBER":
      return { ...state, fetchedNotesOsdNumber: action.payload };
    default:
      return state;
  }
};

// Constants

// Memoized Panel 1 Content Component
const Panel1Content = React.memo<{
  osdDetails: any;
  packageCodeOptions: any[];
  commodityOptions: any[];
  f18NoteMode: boolean;
  formMode: string;
  isSubmitting: boolean;
  f18Note: string;
  f18NoteProNumbers: number[];
  osdNumberState: number | undefined;
  proNumbersList: any[];
  investigationStatusNumber: MisDeliveryStatus | undefined;
  handleClear: () => void;
  handleSubmit: () => void;
  handleF18NoteMode: () => void;
  setF18Note: (note: string) => void;
  setF18NoteProNumbers: (numbers: number[]) => void;
  setLoading: (loading: boolean) => void;
  setFormMode: (mode: string) => void;
  isCleared?:boolean
}>(
  ({
    osdDetails,
    packageCodeOptions,
    commodityOptions,
    f18NoteMode,
    formMode,
    isSubmitting,
    f18Note,
    f18NoteProNumbers,
    osdNumberState,
    proNumbersList,
    investigationStatusNumber,
    handleClear,
    handleSubmit,
    handleF18NoteMode,
    setF18Note,
    setF18NoteProNumbers,
    setLoading,
    setFormMode,
    isCleared
  }) => (
    <div id="panel1bh-content">
      <BasicInformationSection />

      <MisdeliveryInformationSection osdDetails={osdDetails} />

      <PalletInformationSection />

      <ShipmentDetailsSection
        packageTypeOptions={packageCodeOptions}
        commodityOptions={commodityOptions}
      />

      {(f18NoteMode || f18Note || f18NoteProNumbers.length > 0) && (
        <F18NoteSection
          setF18Note={setF18Note}
          setF18NoteProNumbers={setF18NoteProNumbers}
          key={formMode}
          exceptionNumber={osdNumberState}
          f18ProNumbersOptions={proNumbersList}
          setLoading={setLoading}
          setFormMode={setFormMode}
          f18Note={f18Note}
          f18NoteProNumbers={f18NoteProNumbers}
          investigationStatus={investigationStatusNumber}
        />
      )}
      {investigationStatusNumber !==
        MisDeliveryStatus.investigationComplete && !isCleared && (
        <MisdeliveryFormActions
          onClear={handleClear}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          formMode={formMode}
          onF18Note={handleF18NoteMode}
          showF18NoteButton={
            formMode === "view" &&
            proNumbersList?.length > 0 &&
            f18Note?.trim() === ""
          }
        />
      )}
    </div>
  ),
);

// Memoized Panel 2 Component (BOL)
const Panel2Content = React.memo<{
  osdNumber?: number | string;
  misdeliveryEntry: Partial<MisdeliveryBolFormValues>;
  investigationStatus?: MisDeliveryStatus;
  bol?: Partial<MisdeliveryBolFormValues> | null;
  isCleared?: boolean;
  onRefresh?: () => void;
}>(({ osdNumber, misdeliveryEntry, investigationStatus, bol, isCleared, onRefresh }) => (
  <MisdeliveryBOL
    osdNumber={osdNumber}
    misdeliveryEntry={misdeliveryEntry}
    investigationStatus={investigationStatus}
    bol={bol}
    isCleared={isCleared}
    onRefresh={onRefresh}
  />
));

// Memoized Panel 3 Component (Investigation)
const Panel3Content = React.memo<{
  osdNumber: number;
  investigation: IMisdeliveryInvestigation | null | undefined;
  investigationStatus?: MisDeliveryStatus;
  entryBy?: string;
  assignedTo?: string;
  misdeliveredBolPro?: string | number;
  onRefresh: () => void;
  isCleared?: boolean
}>(
  ({
    osdNumber,
    investigation,
    investigationStatus,
    entryBy,
    assignedTo,
    misdeliveredBolPro,
    onRefresh,
    isCleared
  }) => (
    <MisdeliveryInvestigation
      osdNumber={osdNumber}
      investigation={investigation}
      investigationStatus={investigationStatus}
      isExpanded={true}
      entryBy={entryBy}
      assignedTo={assignedTo}
      misdeliveredBolPro={misdeliveredBolPro}
      onRefresh={onRefresh}
      isCleared={isCleared}
    />
  ),
);

// Memoized Panel 4 Component (Upload)
const Panel4Content = React.memo<{
  osdNumber?: number;
  osdType: EOsdType;
  proNumbersList: any[];
  clearedInstruction?: IClearedInstructionContainer | null
}>(({ osdNumber, osdType, proNumbersList, clearedInstruction }) => (
  clearedInstruction?.cleared ? (
    <ClearedInstructionDetails
      clearedInstructionDetails={clearedInstruction}
    />
  ): (
    <UploadDocument
      osdNumber={osdNumber}
      osdType={osdType}
      proNumbersList={proNumbersList}
      uploadKey={EUploadKey.pro}
      heading={{
        proNumber: "Pro #",
        ...APP.documentColumns,
      }}
    />
  ) 
));

const MisdeliveryEntry: React.FC = () => {
  const { osdNumber } = useParams<{ osdNumber: string }>();
  const [osdDetails, setOsdDetails] = useState<any>(null);

  const previousPath = usePreviousPath();
  const [ownership, setOwnership] = useState<{
    entryBy?: string;
    assignedTo?: string;
  } | null>(null);
  const [investigation, setInvestigation] = useState<
    IMisdeliveryInvestigation | null | undefined
  >(null);

  const [bol, setBol] = useState<
    Partial<MisdeliveryBolFormValues> | null | undefined
  >(null);

  const [clearedInstruction, setClearedInstruction] =  useState<
    Partial<IClearedInstructionContainer> | null | undefined
  >(null);

  const [investigationStatusNumber, setInvestigationStatusNumber] =
    useState<MisDeliveryStatus>();

  const proNumbersList = useMemo(
    () =>
      [
        osdDetails?.proNumber,
        osdDetails?.misdeliveredPalletPro,
        investigation?.od400ProNumber,
        bol?.misdeliveredBolPro,
      ].filter(Boolean),
    [
      osdDetails?.proNumber,
      osdDetails?.misdeliveredPalletPro,
      investigation?.od400ProNumber,
      bol?.misdeliveredBolPro,
    ],
  );

  const [osdNumberState, setOsdNumberState] = useState<number | undefined>(
    osdNumber ? Number(osdNumber) : undefined,
  );
  const dispatch = useDispatch();

  // Redux selectors
  const packageCodeOptions = useSelector(
    (state: AppState) => state.packageOptionMasterData,
  );

  const commodityOptions = useSelector(
    (state: AppState) => state.commodityMasterData,
  );
  const hasMisdeliveryInvestigationPermission = useSelector(
    hasMisdeliveryInvestigationPermissionSelector,
  );

  const [f18Note, setF18Note] = React.useState<string>("");
  const [f18NoteProNumbers, setF18NoteProNumbers] = React.useState<number[]>(
    [],
  );
  const [updatedDateTime, setUpdatedDateTime] = React.useState<string>("");
  const [pendingEmailRefresh, setPendingEmailRefresh] = useState(false);
  const formatShortageDateTime = useDateTimeFormatter(setUpdatedDateTime);

  // Consolidated UI state with reducer
  const [uiState, dispatchUI] = React.useReducer(uiStateReducer, {
    expanded: INITIAL_EXPANDED_PANELS,
    isSubmitting: false,
    isLoadingData: false,
    loading: false,
    formMode: osdNumber ? "view" : "entry",
    fetchedOsdNumber: null,
    fetchedNotesOsdNumber: null,
  });

  const {
    expanded,
    isSubmitting,
    isLoadingData,
    loading,
    formMode,
    fetchedOsdNumber,
    fetchedNotesOsdNumber,
  } = uiState;
  const { loadPackageOptions } = usePackageMaster();

  // Track last created values for reset
  const [lastCreatedValues, setLastCreatedValues] =
    useState<MisdeliveryFormData>({
      ...msdInitialForm,
      type: "MSD",
      reportingTerminal: userTerminal(),
    });

  // Form state management using enhanced Formik hook
  const {
    formData: rawFormData,
    errors,
    updateField,
    resetForm,
    validateForm,
    touched,
    formik,
    handleFieldBlur,
    handleChange,
    handleBlur,
  } = useMisdeliveryForm({ ...lastCreatedValues });

  // Centralized reset logic for radio button changes and edit mode
  const resetFieldsOnRadioChange = useCallback(
    (name: keyof MisdeliveryFormData, value: any) => {
      let resetFields: (keyof MisdeliveryFormData)[] = [];
      if (name === "hasProNumber") {
        resetFields = [
          "currentLocationDifferentThanMsdPoint",
          "currentLocationAddress1",
          "currentCompanyName",
          "currentLocationAddress2",
          "currentLocationCity",
          "currentLocationState",
          "currentLocationZip",
          "currentContactName",
          "currentContactPhone",
          "currentContactExt",
          "currentContactEmail",
          "proNumber",
          "originTerminal",
          "destinationTerminal",
          "lastConfirmedManifest",
          "consigneeName",
          "consigneeAddress1",
          "consigneeAddress2",
          "consigneeCity",
          "consigneeState",
          "consigneeZip",
          "pickupTerminal",
          "consigneeAccount",
        ];
      }
      if (name === "isMisdeliveryHazmat") {
        resetFields = ["unNumber"];
      }
      if (name === "hasMisdeliveredPalletPro") {
        resetFields = [
          "misdeliveredPalletPro",
          "palletShipperName",
          "palletConsigneeName",
        ];
      }
      if (resetFields.length) {
        resetFields.forEach((field: keyof MisdeliveryFormData) => {
          updateField(field, "");
        });
      }
    },
    [updateField],
  );

  // Enhanced field change handler to include reset logic
  const handleFieldChangeWithReset = useCallback(
    (name: keyof MisdeliveryFormData, value: any) => {
      updateField(name, value);
      resetFieldsOnRadioChange(name, value);
    },
    [updateField, resetFieldsOnRadioChange],
  );

  // Memoize formData to prevent prop-driven rerenders
  const formData = useMemo(() => rawFormData, [rawFormData]);

  const misdeliveryBolPrefill = useMemo<
    Partial<MisdeliveryBolFormValues>
  >(() => {
    const computed: Partial<MisdeliveryBolFormValues> = {
      // Fallbacks from panel1 (entry) when BOL fields are missing.
      isMisdeliveryHazmat:
        (bol as any)?.isMisdeliveryHazmat ??
        formData.isMisdeliveryHazmat ??
        undefined,
      unNumber: (bol as any)?.unNumber ?? formData.unNumber ?? undefined,
      totalPiecesMisdelivered:
        (bol as any)?.totalPiecesMisdelivered ??
        formData.totalPiecesMisdelivered,
      proNumber: (bol as any)?.proNumber ?? formData.proNumber,
      misdeliveredPalletPro:
        (bol as any)?.misdeliveredPalletPro ?? formData.misdeliveredPalletPro,
    };

    // Prefer the persisted BOL values (panel2) when available.
    return bol ? { ...bol, ...computed } : computed;
  }, [
    bol,
    formData.isMisdeliveryHazmat,
    formData.unNumber,
    formData.totalPiecesMisdelivered,
    formData.proNumber,
    formData.misdeliveredPalletPro,
  ]);

  // API functions
  const getMasters = useCallback(
    (paramString: string) => {
      if (paramString === "packageCode") {
        loadPackageOptions();
      }
    },
    [loadPackageOptions],
  );

  const fetchNotes = useCallback(
    async (osdNum: number, force: boolean = false) => {
        if (!force && (!osdNum || fetchedNotesOsdNumber === osdNum)) return;
        
      try {
        dispatchUI({ type: "SET_LOADING", payload: true });
        const { notes = [] } = await GetMSDNotes(osdNum);
        const data = notes[0] || {};

        setF18Note(data.note || "");
        setF18NoteProNumbers(data.proNumbers || []);

        if (data.note || (data.proNumbers?.length ?? 0) > 0) {
          dispatchUI({ type: "SET_FORM_MODE", payload: "f18Mode" });
        }

        dispatchUI({ type: "SET_FETCHED_NOTES_OSD_NUMBER", payload: osdNum });
      } catch (e) {
        console.error("Failed to fetch MSD Notes", e);
      } finally {
        dispatchUI({ type: "SET_LOADING", payload: false });
      }
    },
    [fetchedNotesOsdNumber],
  );

  // Accordion state management
  const canViewMisdeliveryInvestigation = useCanViewByOwnershipPermission({
    enabledFor: hasMisdeliveryInvestigationPermission,
    entryBy: ownership?.entryBy,
    assignedTo: ownership?.assignedTo,
    gate: Boolean(osdNumberState),
  });

  const handleAccordionChange = useCallback(
    (panel: string) => (_e: React.SyntheticEvent, isExpanded: boolean) => {
      if (
        (["panel2", "panel3", "panel4"].includes(panel) && !osdNumberState) ||
        ("panel3" === panel &&
          investigationStatusNumber === MisDeliveryStatus.exceptionEntry)
      ) {
        return; // Disable to expansion for these panels, util misdelivery entry is created
      }
      if (isExpanded) {
        if (!expanded.includes(panel)) {
          dispatchUI({ type: "SET_EXPANDED", payload: [...expanded, panel] });
        }
      } else {
        dispatchUI({
          type: "SET_EXPANDED",
          payload: expanded.filter((item) => item !== panel),
        });
      }
    },
    [osdNumberState, expanded, investigationStatusNumber],
  );

  const scrollToPanel = useCallback((panelId: string) => {
    // Scroll to panel3 after expansion animation completes
    setTimeout(() => {
      const panel3Element = document.getElementById(panelId);
      if (panel3Element) {
        panel3Element.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }
    }, 300); // Wait for accordion expansion animation
  }, []);

  const handleClear = useCallback(() => {
    if (formMode === "f18Note") return;

    resetForm();

    if (lastCreatedValues) {
      formik.resetForm({
        values: { ...lastCreatedValues, type: "MSD" },
      });
    }
  }, [formMode, resetForm, lastCreatedValues, formik]);

  const handleSubmit = useCallback(async () => {
    const resetFields: string[] = [];
    // Always run validation and mark fields as touched to show errors
    const isFormValid = await validateForm();

    if(formData.hasProNumber === "Y" && formData.proNumber && !formData.destinationTerminal) {
      document?.getElementsByName("proNumber")?.[0]?.focus();
      return;
    }

    if(formData.hasMisdeliveredPalletPro === "Y" && formData.misdeliveredPalletPro && (!formData.palletShipperName || !formData.palletConsigneeName)) {
      document?.getElementsByName("misdeliveredPalletPro")?.[0]?.focus();
      return;
    }

    if (!isFormValid) {
      document?.getElementsByName(Object.keys(errors)?.[0])?.[0]?.focus();
      return;
    }
    if (!formData.callerPhone && !formData.callerEmail?.trim()) {
      document?.getElementsByName("callerPhone")?.[0]?.focus();
      return;
    }

    // Validate currentContactPhone/currentContactEmail - only required when hasProNumber is "Y"
    if (formData.hasProNumber === "Y" && (!formData.currentContactPhone && !formData.currentContactEmail?.trim())) {
      document?.getElementsByName("currentContactPhone")?.[0]?.focus();
      return;
    }

    dispatchUI({ type: "SET_SUBMITTING", payload: true });
    try {
      dispatchUI({ type: "SET_LOADING", payload: true });
      const params = structuredClone(formData);

      // If currentContactName is "SAME" (case-insensitive), replace with callerName
      if (params.currentContactName?.trim().toUpperCase() === "SAME") {
        params.currentContactName = params.callerName;
      }

      if (params.hasProNumber !== "Y") {
        resetFields.push(
          "currentLocationDifferentThanMsdPoint",
          "currentLocationAddress1",
          "currentCompanyName",
          "currentLocationAddress2",
          "currentLocationCity",
          "currentLocationState",
          "currentLocationZip",
          "proNumber",
          "originTerminal",
          "lastConfirmedManifest",
          "currentContactName",
          "currentContactPhone",
          "currentContactExt",
          "currentContactEmail",
        );
      }

      if (params.isMisdeliveryHazmat !== "Y") {
        resetFields.push("unNumber");
      }
      if (params.hasMisdeliveredPalletPro !== "Y") {
        resetFields.push(
          "misdeliveredPalletPro",
          "palletShipperName",
          "palletConsigneeName",
        );
      }

      const numericFields = [
        "lastConfirmedManifest",
        "currentContactPhone",
        "currentContactExt",
        "callerPhone",
        "callerExt",
        "proNumber",
        "misdeliveredPalletPro",
        "totalPiecesMisdelivered",
        "consigneeAccount"
      ];

      const paramsAny = params as Record<string, any>;

      numericFields.forEach((field) => {
        const value = paramsAny[field];
        if (value) {
          const numberValue = Number(value);
          if (!Number.isNaN(numberValue)) {
            paramsAny[field] = numberValue;
          }
        } else {
          paramsAny[field] = undefined;
        }
      });

      // Instead of deleting, set to empty string for schema compatibility
      resetFields.forEach((field) => {
        paramsAny[field] = undefined;
      });

      // API call

      const response = await CreateMisdeliveryEntry(params);
      setOsdNumberState(response.osdNumber);

      dispatch(isFormValueChanged(false));
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.SUCCESS,
          `OS&D # ${response.osdNumber} has been created successfully`,
          Date.now().toString(),
        ),
      );
      // Store last created values and reset form to them
      setLastCreatedValues({
        ...msdInitialForm, // 1️⃣ default values
        ...params, // 2️⃣ override with object1 values
      });

      formik.resetForm({
        values: {
          ...msdInitialForm, // 1️⃣ default values
          ...params, // 2️⃣ override with params values
        },
      });
      dispatchUI({ type: "SET_FORM_MODE", payload: "view" });
      dispatchUI({ type: "SET_EXPANDED", payload: ["panel1", "panel2"] });
      scrollToPanel("panel2bh-header");
      scrollToPanel("pickup-information");

      // Fetch any existing MSD notes immediately after create
      await fetchNotes(response.osdNumber);
    } catch (error: any) {
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.ERROR,
          error,
          Date.now().toString(),
        ),
      );
    } finally {
      dispatchUI({ type: "SET_SUBMITTING", payload: false });
      dispatchUI({ type: "SET_LOADING", payload: false });
    }
  }, [
    validateForm,
    formData,
    dispatch,
    formik,
    errors,
    fetchNotes,
    scrollToPanel,
  ]);

  // F18 Note button handler
  const handleF18NoteMode = useCallback(() => {
    dispatchUI({ type: "SET_FORM_MODE", payload: "f18Mode" });
  }, []);

  // Wrapper for setLoading to maintain compatibility
  const setLoading = useCallback((loading: boolean) => {
    dispatchUI({ type: "SET_LOADING", payload: loading });
  }, []);

  // Wrapper for setFormMode to maintain compatibility
  const setFormMode = useCallback((mode: string) => {
    dispatchUI({ type: "SET_FORM_MODE", payload: mode });
  }, []);

  // Effect for initial data loading
  useEffect(() => {
    dispatch(isFormValueChanged(false));

    if (!packageCodeOptions?.length) {
      getMasters("packageCode");
    }
    if (!commodityOptions?.length) {
      getMasters("commodity");
    }
  }, [
    dispatch,
    getMasters,
    packageCodeOptions?.length,
    commodityOptions?.length,
  ]);

  const fetchMisdeliveryData = useCallback(
    async (osdNum: number, force = false) => {
      if (!osdNum) return;
      if (!force && fetchedOsdNumber === osdNum) return;
      if (isLoadingData) return;

      dispatchUI({ type: "SET_LOADING", payload: true });
      dispatchUI({ type: "SET_LOADING_DATA", payload: true });
      setLoading(true)
      try {
        const response = await getMisDelivery(osdNum.toString());
        const {
          entry,
          investigation,
          bol,
          investigationStatusNumber,
          entryBy,
          assignedTo,
          clearedInstructionDetails
        } = response;

        setClearedInstruction(clearedInstructionDetails)
        // Convert zero values to undefined for BOL numeric fields
        const bolNumericFields: (keyof MisdeliveryBolFormValues)[] = [
          "pickupNumber",
          "pickupPhoneNumber",
          "totalPiecesMisdelivered",
          "weight",
          "proNumber",
          "misdeliveredPalletPro",
          "misdeliveredBolPro",
        ];

        const normalizedBol = normalizeEmptyFields(bol, bolNumericFields);

        setBol(normalizedBol);
        setOwnership({
          entryBy: entryBy?.trim(),
          assignedTo: assignedTo?.trim(),
        });
        formatShortageDateTime(entry ?? {});

        const normalizedInvestigation = normalizeEmptyFields(investigation, [
          "pickupNumber",
          "od400ProNumber",
        ]);
        setInvestigation(normalizedInvestigation);
        setInvestigationStatusNumber(
          investigationStatusNumber ?? MisDeliveryStatus.exceptionEntry,
        );
        if (
          ([MisDeliveryStatus.inProgress ,MisDeliveryStatus.bolGenerated].includes(investigationStatusNumber)) &&
          previousPath === `/misdelivery/bol/${osdNum}/send-email`
        ) {
          dispatchUI({
            type: "SET_EXPANDED",
            payload: ["panel1", "panel2", "panel3"],
          });
          scrollToPanel("panel3bh-header");
          scrollToPanel("investigation-form");
        }
        // Filter out extra fields that shouldn't be in form data
        const formDataFromAPI: MisdeliveryFormData = entry ?? {};

        const formField = Object.keys(
          msdInitialForm,
        ) as (keyof MisdeliveryFormData)[];
        Object.keys(formDataFromAPI).forEach((key) => {
          if (!formField.includes(key as keyof MisdeliveryFormData)) {
            delete (formDataFromAPI as any)[key];
          }
        });

        // Convert zero values to undefined for numeric fields
        const numericFields = [
          "lastConfirmedManifest",
          "currentContactPhone",
          "currentContactExt",
          "callerPhone",
          "callerExt",
          "proNumber",
          "misdeliveredPalletPro",
          "totalPiecesMisdelivered",
        ];

        numericFields.forEach((field) => {
          const value = formDataFromAPI[field as keyof typeof formDataFromAPI];
          if (value === 0 || value === "") {
            (formDataFromAPI as any)[field] = undefined;
          }
        });

        formik.setValues({
          ...msdInitialForm,
          ...formDataFromAPI,
        });

        setLastCreatedValues({
          ...msdInitialForm,
          ...formDataFromAPI,
        });

        formik.resetForm({
          values: {
            ...msdInitialForm,
            ...formDataFromAPI,
          },
        });

        // Preserve any additional, UI-related entry details.
        setOsdDetails({
          ...(formDataFromAPI as any),
        });
        dispatchUI({ type: "SET_FETCHED_OSD_NUMBER", payload: osdNum });
      } catch (error: any) {
        dispatch(
          PopoutNotification(
            NOTIFICATION_TYPE.ERROR,
            typeof error === "string" ? error : `OS&D # ${osdNumber} not found`,
            Date.now().toString(),
          ),
        );
      } finally {
        setLoading(false)
        dispatchUI({ type: "SET_LOADING_DATA", payload: false });
        dispatchUI({ type: "SET_LOADING", payload: false });
      }
    },
    [
      dispatch,
      fetchedOsdNumber,
      formik,
      formatShortageDateTime,
      isLoadingData,
      previousPath,
      scrollToPanel,
      osdNumber,
      setLoading,
    ],
  );

  useEffect(() => {
    const unsubscribe = subscribeMisdeliveryEmailSent((eventOsdNumber) => {
      const currentOsd =
        osdNumberState?.toString() ?? osdNumber?.toString().trim() ?? "";

      if (eventOsdNumber === currentOsd) {
        setPendingEmailRefresh(true);
      }
    });

    return unsubscribe;
  }, [osdNumber, osdNumberState]);

  useEffect(() => {
    const currentOsd =
      osdNumberState?.toString() ?? osdNumber?.toString().trim() ?? "";

    if (!currentOsd) return;

    if (consumeMisdeliveryEmailSentFlag(currentOsd)) {
      setPendingEmailRefresh(true);
    }
  }, [osdNumber, osdNumberState]);

  // Effect to fetch misdelivery data when osdNumberState is available
  useEffect(() => {
    /* NOSONAR */
    if (pendingEmailRefresh && isLoadingData) {
      // Keep the pending flag set and re-run once current loading completes.
      return;
    }

    if (
      osdNumberState &&
      (formMode === "view" || formMode === "edit") &&
      (pendingEmailRefresh || fetchedOsdNumber !== osdNumberState)
    ) {
      fetchMisdeliveryData(osdNumberState, pendingEmailRefresh);

      fetchNotes(osdNumberState, pendingEmailRefresh);
      if (pendingEmailRefresh) {
        setPendingEmailRefresh(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [osdNumberState, formMode, fetchNotes, pendingEmailRefresh, isLoadingData]);

  // Continuously track form changes and update Redux state
  useEffect(() => {
    const hasChanged = !compareObjects(formData, lastCreatedValues);

    dispatch(isFormValueChanged(hasChanged));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, lastCreatedValues, dispatch]);

  // Use callbacks directly - no need for extra wrapper
  const memoizedOnFieldChange = handleFieldChangeWithReset;
  const memoizedOnFieldBlur = handleFieldBlur;

  // Determine disabled state for form fields
  let formDisabled = false;
  let f18NoteMode = false;
  if (formMode === "view" || isLoadingData) {
    formDisabled = true;
  } else if (formMode === "f18Mode") {
    formDisabled = true;
    f18NoteMode = true;
  }

  // Hide validation messages after create/view/edit flows.
  const suppressValidationMessages =
    Boolean(osdNumberState) || investigationStatusNumber !== undefined;

  // Memoize colorClass based only on investigationStatusNumber
  const colorClass = useCallback(
    (panelNumber: string): string => {
      const baseClass = "queue-check";

      if(clearedInstruction?.cleared) {
         return `${baseClass} queue-check-gc`;
      }

      if (!investigationStatusNumber || panelNumber === "4") {
        return baseClass;
      }

      const isComplete =
        investigationStatusNumber === MisDeliveryStatus.investigationComplete;

      const isEntryCreated = panelNumber === "1";

      const isBOLGenerated =
        panelNumber === "2" && investigationStatusNumber !==  MisDeliveryStatus.exceptionEntry;

      if (isComplete || isEntryCreated || isBOLGenerated) {
        return `${baseClass} queue-check-gc`;
      }

      return baseClass;
    },
    [investigationStatusNumber, clearedInstruction?.cleared],
  );

  // Helper: Get panel number display
  const getPanelNumber = useCallback(
    (panelId: string, panelNumber: string): number => {
      if (panelId === "panel4" && !canViewMisdeliveryInvestigation) {
        return 3;
      }
      return Number(panelNumber);
    },
    [canViewMisdeliveryInvestigation],
  );

  // Helper: Render panel content based on panel ID
  const renderPanelContent = useCallback(
    (panelId: string) => {
      switch (panelId) {
        case "panel1":
          return (
            <Panel1Content
              osdDetails={osdDetails}
              packageCodeOptions={packageCodeOptions}
              commodityOptions={commodityOptions}
              f18NoteMode={f18NoteMode}
              formMode={formMode}
              isSubmitting={isSubmitting}
              f18Note={f18Note}
              f18NoteProNumbers={f18NoteProNumbers}
              osdNumberState={osdNumberState}
              proNumbersList={proNumbersList}
              investigationStatusNumber={investigationStatusNumber}
              handleClear={handleClear}
              handleSubmit={handleSubmit}
              handleF18NoteMode={handleF18NoteMode}
              setF18Note={setF18Note}
              setF18NoteProNumbers={setF18NoteProNumbers}
              setLoading={setLoading}
              setFormMode={setFormMode}
              isCleared={clearedInstruction?.cleared}
            />
          );
        case "panel2":
          return expanded.includes("panel2") ? (
            <Panel2Content
              osdNumber={osdNumberState}
              misdeliveryEntry={misdeliveryBolPrefill}
              investigationStatus={investigationStatusNumber}
              bol={bol}
              isCleared={clearedInstruction?.cleared}
              onRefresh={() => {
                if (osdNumberState) {
                  fetchMisdeliveryData(osdNumberState, true);
                  fetchNotes(osdNumberState, true);
                }
              }}
            />
          ) : null;

        case "panel3":
          return expanded.includes("panel3") && osdNumberState ? (
            <Panel3Content
              key={panelId}
              osdNumber={Number(osdNumberState)}
              investigation={investigation}
              investigationStatus={investigationStatusNumber}
              misdeliveredBolPro={bol?.misdeliveredBolPro}
              entryBy={ownership?.entryBy}
              assignedTo={ownership?.assignedTo}
              onRefresh={() => 
                {
                  fetchMisdeliveryData(osdNumberState, true);
                  fetchNotes(osdNumberState, true);
                }}
              isCleared={clearedInstruction?.cleared}
            />
          ) : null;

        case "panel4":
          return expanded.includes("panel4") ? (
            <Panel4Content
              osdNumber={osdNumber ? Number(osdNumber) : undefined}
              osdType={EOsdType.msd}
              proNumbersList={proNumbersList}
              clearedInstruction={clearedInstruction}
            />
          ) : null;

        default:
          return (
            <Typography variant="body1">
              Panel content not implemented yet
            </Typography>
          );
      }
    },
    [
      bol,
      expanded,
      osdDetails,
      packageCodeOptions,
      commodityOptions,
      f18NoteMode,
      formMode,
      isSubmitting,
      f18Note,
      f18NoteProNumbers,
      osdNumberState,
      proNumbersList,
      investigationStatusNumber,
      handleClear,
      handleSubmit,
      handleF18NoteMode,
      setF18Note,
      setF18NoteProNumbers,
      setLoading,
      setFormMode,
      misdeliveryBolPrefill,
      investigation,
      ownership,
      fetchMisdeliveryData,
      osdNumber,
      clearedInstruction,
      fetchNotes
    ],
  );

  // Memoized accordion structure - panel content handled separately
  const accordionPanels = useMemo(
    () =>
      ACCORDION_PANELS.filter(
        (panel) => panel.id !== "panel3" || canViewMisdeliveryInvestigation,
      ).map((panel) => (
        <Accordion
          key={panel.id}
          className="accordion"
          expanded={expanded.includes(panel.id)}
          onChange={handleAccordionChange(panel.id)}
        >
          <AccordionSummary
            className="header"
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${panel.id}bh-content`}
            id={`${panel.id}bh-header`}
          >
            <Typography className={colorClass(panel.number)}>
              {getPanelNumber(panel.id, panel.number)}
            </Typography>
            <Typography className="accordion-header">{panel.id === "panel4" && clearedInstruction?.cleared  ?  "Cleared Instruction Details": panel.title}</Typography>
            {panel.id === "panel2" && (
              <Typography className="header-notice note-scroll">
                <div className="animate">
                  If PICKUP IS RESCHEDULED, DO NOT NEED TO RESEND BOL.
                </div>
              </Typography>
            )}
          </AccordionSummary>
          <AccordionDetails className="accordion-container">
            {renderPanelContent(panel.id)}
          </AccordionDetails>
        </Accordion>
      )),
    [
      expanded,
      canViewMisdeliveryInvestigation,
      colorClass,
      getPanelNumber,
      handleAccordionChange,
      renderPanelContent,
      clearedInstruction?.cleared
    ],
  );

  // Memoize form context value to prevent unnecessary re-renders
  const formContextValue = useMemo(
    () => ({
      formData,
      errors: suppressValidationMessages ? ({} as typeof errors) : errors,
      touched: suppressValidationMessages ? ({} as typeof touched) : touched,
      onFieldChange: memoizedOnFieldChange,
      onFieldBlur: memoizedOnFieldBlur,
      handleChange,
      handleBlur,
      setFieldTouched: formik.setFieldTouched,
      disabled: formDisabled,
    }),
    [
      formData,
      errors,
      touched,
      suppressValidationMessages,
      memoizedOnFieldChange,
      memoizedOnFieldBlur,
      handleChange,
      handleBlur,
      formik.setFieldTouched,
      formDisabled,
    ],
  );

  // Effect for edit mode: reset fields if values require it, but only when entering edit mode
  const prevFormModeRef = React.useRef(formMode);
  useEffect(() => {
    if (formMode === "edit" && prevFormModeRef.current !== "edit") {
      if (formData.hasProNumber === "N") {
        resetFieldsOnRadioChange("hasProNumber", formData.hasProNumber);
      }
      if (formData.isMisdeliveryHazmat === "N") {
        resetFieldsOnRadioChange(
          "isMisdeliveryHazmat",
          formData.isMisdeliveryHazmat,
        );
      }
      if (formData.canProvidePhotos === "N") {
        resetFieldsOnRadioChange("canProvidePhotos", formData.canProvidePhotos);
      }
      if (formData.hasMisdeliveredPalletPro === "N") {
        resetFieldsOnRadioChange(
          "hasMisdeliveredPalletPro",
          formData.hasMisdeliveredPalletPro,
        );
      }
    }
    prevFormModeRef.current = formMode;
  }, [formMode, formData, resetFieldsOnRadioChange]);

  return (
    <MisdeliveryFormProvider value={formContextValue}>
      <div className="bg-layout overage-entry">
        <PageHeaderWithBack
          osdNumber={osdNumberState}
          proNumber={osdDetails?.proNumber}
          headingTitle="Misdelivery Entry"
          updatedDateTime={updatedDateTime}
        />

        <div className="content-area">{accordionPanels}</div>
      </div>
      <Loader pageLoader={loading}></Loader>
    </MisdeliveryFormProvider>
  );
};

export default MisdeliveryEntry;
